/* ============================================================
   Section: Video Reviews Grid
   Hover-to-play, IntersectionObserver auto-play, YouTube lightbox
   ============================================================ */

(function () {
  'use strict';

  /* ─── Named Constants ─── */
  var PARALLAX_SPEED = 0.15;
  var VIDEO_OBSERVER_THRESHOLD = 0.2;
  var YT_OBSERVER_THRESHOLD = 0.3;
  var HEADER_OBSERVER_THRESHOLD = 0.2;
  var ENTRANCE_OBSERVER_THRESHOLD = 0.1;
  var ENTRANCE_STAGGER_DELAY = 0.12;           /* seconds between cards */
  var ENTRANCE_STAGGER_BATCH_MS = 50;           /* ms to batch viewport entries */
  var ENTRANCE_CLEANUP_BASE_MS = 800;           /* ms base for transition-delay cleanup */
  var ENTRANCE_CLEANUP_PER_CARD_MS = 120;       /* ms per card for cleanup delay */
  var HEADER_ANIM_STEP_MS = 200;                /* ms between header element reveals */
  var YT_EMBED_FADE_DELAY_MS = 800;             /* ms delay before showing YT embed */
  var MODAL_CLOSE_DELAY_MS = 350;               /* ms for modal close transition */

  function initSection(section) {
    if (!section || section.dataset.vrgInitialized) return;
    section.dataset.vrgInitialized = 'true';

    var sectionId = section.dataset.sectionId;
    var modal = document.getElementById('vrg-modal-' + sectionId);
    if (!modal) return;

    var cards = section.querySelectorAll('.vrg-card__trigger');

    /* ─── Hover play/pause for mp4 preview videos ─── */
    cards.forEach(function (btn) {
      var video = btn.querySelector('.vrg-card__video');
      if (!video) return;

      btn.addEventListener('mouseenter', function () {
        video.play().catch(function () {});
      });

      btn.addEventListener('mouseleave', function () {
        if (btn.classList.contains('is-autoplaying')) return;
        video.pause();
        video.currentTime = 0;
      });
    });

    /* ─── YouTube inline auto-play helpers ─── */
    function stopYouTube(btn) {
      if (!btn) return;
      var embedDiv = btn.querySelector('.vrg-card__yt-embed');
      if (!embedDiv) return;
      var existing = embedDiv.querySelector('iframe');
      if (existing) {
        existing.src = '';
        existing.remove();
      }
      btn.classList.remove('is-yt-playing');
    }

    function playYouTube(btn) {
      if (!btn) return;
      var ytId = btn.dataset.youtubeId;
      if (!ytId) return;
      var embedDiv = btn.querySelector('.vrg-card__yt-embed');
      if (!embedDiv) return;
      if (embedDiv.querySelector('iframe')) return;

      var ytIframe = document.createElement('iframe');
      ytIframe.src = 'https://www.youtube.com/embed/' + ytId +
        '?autoplay=1&mute=1&loop=1&playlist=' + ytId +
        '&controls=0&rel=0&playsinline=1&modestbranding=1&disablekb=1';
      ytIframe.allow = 'autoplay; encrypted-media';
      ytIframe.setAttribute('frameborder', '0');
      ytIframe.title = '';
      embedDiv.appendChild(ytIframe);
      ytIframe.addEventListener('load', function () {
        setTimeout(function () {
          btn.classList.add('is-yt-playing');
        }, YT_EMBED_FADE_DELAY_MS);
      });
      ytIframe.addEventListener('error', function () {
        /* Silently handle YouTube iframe load failures */
        btn.classList.remove('is-yt-playing');
      });
    }

    /* ─── Create embed containers on YouTube cards ─── */
    cards.forEach(function (btn) {
      if (!btn.querySelector('.vrg-card__video') && btn.dataset.youtubeId) {
        var embedDiv = document.createElement('div');
        embedDiv.className = 'vrg-card__yt-embed';
        btn.appendChild(embedDiv);
      }
    });

    /* ─── IntersectionObserver: auto-play mp4 previews ─── */
    if ('IntersectionObserver' in window) {
      var videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var btn = entry.target;
            var video = btn.querySelector('.vrg-card__video');
            if (!video) return;
            if (entry.isIntersecting) {
              btn.classList.add('is-autoplaying');
              video.play().catch(function () {});
            } else {
              btn.classList.remove('is-autoplaying');
              video.pause();
            }
          });
        },
        { threshold: VIDEO_OBSERVER_THRESHOLD }
      );
      cards.forEach(function (btn) {
        if (btn.querySelector('.vrg-card__video')) {
          videoObserver.observe(btn);
        }
      });
    }

    /* ─── IntersectionObserver: auto-play YouTube embeds ─── */
    if ('IntersectionObserver' in window) {
      var ytObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var btn = entry.target;
            if (entry.isIntersecting) {
              playYouTube(btn);
            } else {
              stopYouTube(btn);
            }
          });
        },
        { threshold: YT_OBSERVER_THRESHOLD }
      );
      cards.forEach(function (btn) {
        if (!btn.querySelector('.vrg-card__video') && btn.dataset.youtubeId) {
          ytObserver.observe(btn);
        }
      });
    }

    /* ─── Header entrance animation ─── */
    var header = section.querySelector('.vrg-header');
    if (header && 'IntersectionObserver' in window) {
      var headerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;

            // Staggered reveal: badge → heading words → subtitle
            var badge = header.querySelector('.vrg-badge');
            var words = header.querySelectorAll('.vrg-heading-word');
            var subtitle = header.querySelector('.vrg-subtitle');
            var delay = 0;

            if (badge) {
              setTimeout(function () { badge.classList.add('vrg-anim--visible'); }, delay);
              delay += HEADER_ANIM_STEP_MS;
            }
            words.forEach(function (word, i) {
              setTimeout(function () { word.classList.add('vrg-anim--visible'); }, delay + i * HEADER_ANIM_STEP_MS);
            });
            delay += words.length * HEADER_ANIM_STEP_MS;
            if (subtitle) {
              setTimeout(function () { subtitle.classList.add('vrg-anim--visible'); }, delay);
            }

            headerObserver.unobserve(entry.target);
          });
        },
        { threshold: HEADER_OBSERVER_THRESHOLD }
      );
      headerObserver.observe(header);
    } else if (header) {
      // Fallback: show everything immediately
      var allAnimEls = header.querySelectorAll('.vrg-badge, .vrg-heading-word, .vrg-subtitle');
      allAnimEls.forEach(function (el) { el.classList.add('vrg-anim--visible'); });
    }

    /* ─── Scroll-triggered card entrance animation ─── */
    if ('IntersectionObserver' in window) {
      var pendingCards = [];
      var staggerTimer = null;

      function flushPending() {
        staggerTimer = null;
        var batch = pendingCards.slice();
        pendingCards = [];
        batch.forEach(function (card, i) {
          card.style.transitionDelay = (i * ENTRANCE_STAGGER_DELAY) + 's';
          card.classList.add('vrg-card--visible');
          // Clean up inline delay after animation completes
          setTimeout(function () {
            card.style.transitionDelay = '';
          }, ENTRANCE_CLEANUP_BASE_MS + i * ENTRANCE_CLEANUP_PER_CARD_MS);
        });
      }

      var entranceObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              pendingCards.push(entry.target);
              entranceObserver.unobserve(entry.target);
              // Batch cards that enter the viewport together
              if (staggerTimer) clearTimeout(staggerTimer);
              staggerTimer = setTimeout(flushPending, ENTRANCE_STAGGER_BATCH_MS);
            }
          });
        },
        { threshold: ENTRANCE_OBSERVER_THRESHOLD }
      );
      section.querySelectorAll('.vrg-card').forEach(function (card) {
        entranceObserver.observe(card);
      });
    } else {
      section.querySelectorAll('.vrg-card').forEach(function (card) {
        card.classList.add('vrg-card--visible');
      });
    }

    /* ─── Background Text Parallax Scroll ─── */
    var bgText = section.querySelector('.vrg-bg-text');
    if (bgText && 'IntersectionObserver' in window) {
      var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!prefersReducedMotion) {
        var scrollHandler = null;

        var bgObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                scrollHandler = function () {
                  var rect = section.getBoundingClientRect();
                  var offset = rect.top * PARALLAX_SPEED;
                  bgText.style.transform = 'translateY(' + offset + 'px)';
                };
                window.addEventListener('scroll', scrollHandler, { passive: true });
                scrollHandler(); /* apply immediately */
              } else {
                if (scrollHandler) {
                  window.removeEventListener('scroll', scrollHandler);
                  scrollHandler = null;
                }
              }
            });
          },
          { threshold: 0 }
        );
        bgObserver.observe(section);

        /* Store for cleanup */
        section._vrgBgObserver = bgObserver;
        section._vrgScrollHandler = function () { return scrollHandler; };
      }
    }

    /* ─── Store observers for cleanup on section reload ─── */
    section._vrgObservers = [videoObserver, ytObserver, headerObserver, entranceObserver, section._vrgBgObserver].filter(Boolean);

    /* ─── Lightbox Modal ─── */
    var iframe = modal.querySelector('.vrg-modal__iframe');
    var iframeWrap = modal.querySelector('.vrg-modal__iframe-wrap');
    var modalInner = modal.querySelector('.vrg-modal__inner');
    var closeBtn = modal.querySelector('.vrg-modal__close');
    var backdrop = modal.querySelector('.vrg-modal__backdrop');
    var previousFocus = null;

    /* Handle YouTube iframe load failures in the lightbox */
    if (iframe) {
      iframe.addEventListener('error', function () {
        /* Silently handle — the user sees a black box, modal can still be closed */
      });
    }

    function openModal(youtubeId, title, isShort) {
      if (!youtubeId) return;

      iframeWrap.classList.toggle('is-wide', !isShort);
      modalInner.classList.toggle('is-portrait', !!isShort);

      iframe.src =
        'https://www.youtube.com/embed/' +
        youtubeId +
        '?autoplay=1&rel=0&playsinline=1';
      if (title) iframe.title = title;

      previousFocus = document.activeElement;
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('is-open');
          closeBtn.focus();
        });
      });
    }

    function closeModal() {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';

      setTimeout(function () {
        modal.setAttribute('hidden', '');
        iframe.src = '';
        if (previousFocus) previousFocus.focus();
      }, MODAL_CLOSE_DELAY_MS);
    }

    cards.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var ytId = btn.dataset.youtubeId;
        var ytTitle = btn.dataset.youtubeTitle;
        var isShort = btn.dataset.isShort === 'true';
        openModal(ytId, ytTitle, isShort);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });
  }

  /* ─── Auto-init on page load ─── */
  function initAll() {
    document.querySelectorAll('.vrg-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ─── Shopify theme editor support ─── */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('.vrg-section');
    if (section) {
      /* Disconnect all existing observers before reinitializing */
      if (section._vrgObservers) {
        section._vrgObservers.forEach(function (obs) {
          obs.disconnect();
        });
        section._vrgObservers = null;
      }
      /* Clean up scroll listener if active */
      if (section._vrgScrollHandler) {
        var handler = section._vrgScrollHandler();
        if (handler) {
          window.removeEventListener('scroll', handler);
        }
        section._vrgScrollHandler = null;
      }
      section.removeAttribute('data-vrg-initialized');
      initSection(section);
    }
  });
})();
